import { Popover, Stack, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import useAuth from 'hooks/useAuth';
import useClinicDocument from 'hooks/useClinicDocument';
import useClinicDocuments from 'hooks/useClinicDocuments';
import useFormBuilder from 'hooks/useFormBuilder';
import PopupState, { bindPopover, bindTrigger } from 'material-ui-popup-state';
import { useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router';
import { store } from 'store';
import { SET_CREATE_FORM_FIELDS, SET_ITEMS, SET_STEPS, SET_TITLE } from 'store/actions';
import DocumentCover from '../announcement/DocumentCover';
import BasicTable from '../BasicTable';
import PopularCard from '../skeleton/PopularCard';
import DocumentActions from './DocumentActions';

const ManageDocuments = ({ documentId, showDocumentActions = true, selectImageForUpload, getDocument, getCustomForm, hideCustomForm }) => {
  const intl = useIntl();
  const navigate = useNavigate();

  const { refetchDocuments } = useClinicDocuments();
  const { Documents, isLoadingDocuments, customForms, isLoadingCustomForms } = useClinicDocument({ documentId });
  const { userId } = useAuth();
  const { userForms, forms, userFormsIsLoading, refetch, updateForm, refetchForms } = useFormBuilder({ userId });
  // const { }
  const findForm = useCallback(
    (id) => {
      // console.log('forms are ', forms);
      if (!forms) {
        return undefined;
      }
      // Once forms are available, search for the form by id
      return forms.find((form) => form?._id === id);
    },
    [forms]
  );
  const documentTablecolumns: GridColDef[] = useMemo(
    () => [
      {
        field: 'documents',
        headerName: intl.formatMessage({ defaultMessage: 'Documents' }),
        // width: 200,
        renderCell: (params: GridRenderCellParams) => {
          const { link, _id, key, name, contentType } = params.row;

          return (
            <>
              <Stack direction={'row'} spacing={2} flexWrap={'wrap'}>
                <DocumentCover key={key} _id={_id} name={name} link={link} contentType={contentType} />
              </Stack>
            </>
          );
        }
      },
      {
        field: 'targetAudience',
        headerName: intl.formatMessage({ defaultMessage: 'Target Audience' }),
        valueGetter: (_, row: any) => {
          return row.targetAudience.toString();
        },
        renderCell: (params: GridRenderCellParams) => {
          const row = params.row;
          return (
            <PopupState variant="popover" popupId="demo-popup-popover">
              {(popupState) => (
                <>
                  {row.targetAudience.length > 1 ? (
                    <Button {...bindTrigger(popupState)} sx={{ color: 'grey' }}>
                      {row.targetAudience[0]}, ...
                    </Button>
                  ) : (
                    <Button {...bindTrigger(popupState)} sx={{ color: 'grey' }}>
                      {row.targetAudience[0]}
                    </Button>
                  )}
                  <Popover
                    {...bindPopover(popupState)}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'center'
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'center'
                    }}
                  >
                    {row.targetAudience.map((item) => (
                      <Typography key={item} sx={{ m: 2 }}>
                        {item}
                      </Typography>
                    ))}
                  </Popover>
                </>
              )}
            </PopupState>
          );
        }
      },
      {
        field: 'actions',
        headerName: intl.formatMessage({ defaultMessage: 'Actions' }),
        width: 240,
        sortable: false,
        renderCell: (params) => {
          return showDocumentActions ? (
            <DocumentActions docId={params.row._id} refetchDocuments={refetchDocuments} documentId={documentId} />
          ) : (
            <Button
              variant="contained"
              onClick={() => {
                // console.log('here');
                if (selectImageForUpload) {
                  return selectImageForUpload.mutateAsync({ clinicDocumentItemId: params.row._id });
                }

                return getDocument(params.row._id, params.row.name, params.row.link, params.row.contentType, documentId);
              }}
            >
              <FormattedMessage defaultMessage={'Select'} />
            </Button>
          );
        }
      }
    ],
    [documentId, intl, refetchDocuments, selectImageForUpload, showDocumentActions, getDocument]
  );

  const customFormColumns: GridColDef[] = useMemo(
    () => [
      {
        field: 'title',
        headerName: intl.formatMessage({ defaultMessage: 'Title' }),
        // width: 200,
        renderCell: (params: GridRenderCellParams) => {
          return <Typography>{params.row.form?.name ?? ''}</Typography>;
        }
      },
      {
        field: 'targetAudience',
        headerName: intl.formatMessage({ defaultMessage: 'Target Audience' }),
        valueGetter: (_, row: any) => {
          return row.targetAudience.toString();
        },
        renderCell: (params: GridRenderCellParams) => {
          const row = params.row;
          return (
            <PopupState variant="popover" popupId="demo-popup-popover">
              {(popupState) => (
                <>
                  {row.targetAudience.length > 1 ? (
                    <Button {...bindTrigger(popupState)} sx={{ color: 'grey' }}>
                      {row.targetAudience[0]}, ...
                    </Button>
                  ) : (
                    <Button {...bindTrigger(popupState)} sx={{ color: 'grey' }}>
                      {row.targetAudience[0]}
                    </Button>
                  )}
                  <Popover
                    {...bindPopover(popupState)}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'center'
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'center'
                    }}
                  >
                    {row.targetAudience.map((item) => (
                      <Typography key={item} sx={{ m: 2 }}>
                        {item}
                      </Typography>
                    ))}
                  </Popover>
                </>
              )}
            </PopupState>
          );
        }
      },
      // {
      //   field: 'status',
      //   headerName: intl.formatMessage({ defaultMessage: 'Status' }),
      //   // width: 200,
      //   renderCell: (params: GridRenderCellParams) => {
      //     return (
      //       <Typography sx={{ color: params.row.form?.isDraft ? '#333' : '#f00' }}>
      //         {params.row.form?.isDraft
      //           ? intl.formatMessage({ defaultMessage: 'Complete' })
      //           : intl.formatMessage({ defaultMessage: 'Draft' })}
      //       </Typography>
      //     );
      //   }
      // },
      {
        field: 'actions',
        headerName: intl.formatMessage({ defaultMessage: 'Actions' }),
        width: 240,
        sortable: false,
        renderCell: (params) => {
          const { form: selectedForm } = params.row;
          // console.log('selected form is ', selectedForm);

          return showDocumentActions ? (
            <Button
              onClick={() => {
                const form: any = findForm(selectedForm?._id);
                // console.log('form found ', form);
                const { type, steps, name, description, _id, lng } = form;
                // console.log('row is ', form?._id);
                steps[0] = {
                  title: name,
                  items: steps[0]?.items ?? []
                };
                const createFormFields = {
                  name,
                  description,
                  template: _id,
                  lng,
                  templateAvailableTo: {
                    providerAdministratorS: form?.templateAvailableTo?.providerAdministratorS,
                    providerAdministratorD: form?.templateAvailableTo?.providerAdministratorD,
                    provider: form?.templateAvailableTo?.provider,
                    administrator: form?.templateAvailableTo?.administrator
                  },
                  type
                };
                store.dispatch({ type: SET_CREATE_FORM_FIELDS, payload: createFormFields });
                store.dispatch({
                  type: SET_ITEMS,
                  payload: form?.type === 'default' ? form?.items : form?.steps?.length ? form?.steps[0]?.items : []
                });
                store.dispatch({ type: SET_TITLE, payload: name });
                store.dispatch({ type: SET_STEPS, payload: steps });
                navigate(`/forms/${type === 'stepper' ? 'wizard' : 'default'}`, {
                  state: {
                    isFromStep: true,
                    isFromTemplate: false,
                    isEditing: true,
                    isClinicDocuments: true,
                    formId: _id
                  }
                });
              }}
            >
              <FormattedMessage defaultMessage="Edit Form" />
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={() => {
                return getCustomForm && getCustomForm(params.row.form._id, params.row.form.name);
              }}
            >
              <FormattedMessage defaultMessage="Select" />
            </Button>
          );
        }
      }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [intl, showDocumentActions, navigate, getCustomForm, forms]
  );

  return (
    <>
      {/* {hideCustomForm && (
        <>
          <Typography variant="h4" mt={8} mb={2}>
            <FormattedMessage defaultMessage="Custom Forms" />
          </Typography>
          {isLoadingCustomForms ? (
            <PopularCard />
          ) : (
            <BasicTable
              emptyMessage={<FormattedMessage defaultMessage="No forms found" />}
              columns={customFormColumns}
              rows={customForms?.forms}
              getRowId={(row) => row._id}
              uniqueBy="_id"
              pagination={false}
              rowCount={customForms?.forms?.length || 0}
              hideFooter
              hideSearch
            />
          )}
        </>
      )} */}
      <Typography variant="h4" mt={4} mb={2}>
        <FormattedMessage defaultMessage="Documents" />
      </Typography>
      {isLoadingDocuments ? (
        <PopularCard />
      ) : (
        <BasicTable
          emptyMessage={<FormattedMessage defaultMessage="No documents found" />}
          columns={documentTablecolumns}
          rows={Documents?.documents ?? []}
          getRowId={(row) => row._id}
          uniqueBy="_id"
          pagination={false}
          rowCount={Documents?.totalRows || 0}
          hideFooter
          hideSearch
        />
      )}
    </>
  );
};

export default ManageDocuments;
